import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Combobox, Flex, Icon, Label, Text } from '@kognitos/lattice';

import {
  Bdkv2OAuthFlow,
  type Bdkv2BookAuthenticationDescriptor,
  type Bdkv2BookDescriptor,
} from '@/generated/hooks/bdk/models';
import type { V1OAuthApp } from '@/generated/hooks/gringotts/models';
import {
  EOAuthGrant,
  RegisterOAuthAppDialog,
  type IRegisterOAuthAppDefaults,
} from '@/modules/credentials';
import {
  selectCurrentOrganizationId,
  useUserPreferencesStore,
} from '@/shared/stores/user-preferences.store';

/** Props for {@link OAuthAppPicker}. */
export interface IOAuthAppPickerProps {
  integration: Bdkv2BookDescriptor;
  /** The book's own OAuth method the app signs in for. */
  authentication: Bdkv2BookAuthenticationDescriptor;
  /** The method's secret type; a registered app is linked to it. */
  secretTypeName: string;
  /** The organization's and workspace's apps for this secret type. */
  apps: V1OAuthApp[];
  selectedAppId?: string;
  onSelect: (appId: string) => void;
  /** Whether the user may register organization-wide apps. */
  canChooseScope: boolean;
  /** Shown when the user tries to continue without an app. */
  error?: string;
}

/**
 * Picks the OAuth app a new credential signs in through, among the apps the
 * organization and workspace registered for the method's secret type. The
 * last option registers a new one, prefilled from the book's OAuth settings.
 *
 * @param props - Component props.
 * @returns The OAuth app picker.
 */
export function OAuthAppPicker({
  integration,
  authentication,
  secretTypeName,
  apps,
  selectedAppId,
  onSelect,
  canChooseScope,
  error,
}: IOAuthAppPickerProps) {
  const { t } = useTranslation('credentials');
  const organizationId = useUserPreferencesStore(selectCurrentOrganizationId);
  const [isRegistering, setIsRegistering] = useState(false);

  const oauth = authentication.oauth ?? {};
  const integrationName = integration.display_name || integration.name || '';
  const registerDefaults: IRegisterOAuthAppDefaults = {
    displayName: t('credentials.oauthApp.defaultName', {
      integration: integrationName,
    }),
    grant: oauth.flows?.includes(Bdkv2OAuthFlow.OAuthFlowAuthorizationCode)
      ? EOAuthGrant.AuthorizationCode
      : EOAuthGrant.ClientCredentials,
    authorizeUrl: oauth.authorize_endpoint,
    tokenUrl: oauth.token_endpoint,
    scopes: oauth.scope,
    // A book may send its scopes under another parameter (Slack: `user_scope`).
    scopeParameter: oauth.parameter_mapping?.scope,
    connectionArguments: (oauth.arguments ?? []).map(argument => ({
      id: argument.id ?? '',
      label: argument.name || argument.id || '',
    })),
  };

  const scopeLabel = (app: V1OAuthApp) =>
    app.scope === `organizations/${organizationId}`
      ? t('credentials.scope.organization')
      : t('credentials.scope.workspace');

  return (
    <>
      <Flex direction="column" align="stretch" gap={2} width="full">
        <Label>{t('credentials.oauthApp.label')}</Label>
        {apps.length > 0 ? (
          <Combobox
            options={apps.map(app => ({
              value: app.id ?? '',
              label: app.display_name || app.name || '',
              description: scopeLabel(app),
            }))}
            value={selectedAppId ?? ''}
            onValueChange={onSelect}
            placeholder={t('credentials.oauthApp.placeholder')}
            showDescriptions
            showClear={false}
            createOption={{
              label: t('credentials.oauthApp.registerNew'),
              onSelect: () => setIsRegistering(true),
            }}
            className="w-full"
            popoverClassName="w-[var(--radix-popover-trigger-width)]"
            modal
          />
        ) : (
          <Flex direction="column" align="start" gap={2}>
            <Text level="small" color="muted">
              {t('credentials.oauthApp.none', { integration: integrationName })}
            </Text>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setIsRegistering(true)}
            >
              <Icon type="Plus" size="sm" />
              {t('credentials.oauthApp.registerNew')}
            </Button>
          </Flex>
        )}
        {error && (
          <Text level="small" className="text-destructive">
            {error}
          </Text>
        )}
      </Flex>

      {isRegistering && (
        <RegisterOAuthAppDialog
          isOpen
          onClose={() => setIsRegistering(false)}
          onRegistered={app => onSelect(app.id ?? '')}
          secretTypeName={secretTypeName}
          defaults={registerDefaults}
          lockGrant
          canChooseScope={canChooseScope}
        />
      )}
    </>
  );
}
